/**
 * ProjectCard — a single project tile in the project grid.
 *
 * Handles both real projects and the synthetic Inbox card (passed in as
 * `inbox` mode). Inbox can be clicked into but cannot be
 * renamed/deleted — there's no row in the table.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PaletteIcon from '@mui/icons-material/Palette';
import type { Project } from '../../types/project';

import './ProjectCard.css';

export interface ProjectCardProps {
  /** Real project, or null for the synthetic Inbox card. */
  project: Project | null;
  /** Number of flows in this project (or Inbox). */
  flowCount: number;
  /** Most recent flow's thumbnail URL, if any. */
  coverThumbnail?: string | null;
  onClick: () => void;
  onRename?: () => void;
  onDelete?: () => void;
  onArchive?: () => void;
  /** Index for staggered animation. */
  animationIndex?: number;
}

// Visual style for the default bucket. User-facing name is "General" — it
// reads as a place alongside the user's own projects, which is what it is.
// The internal sentinel stays INBOX_PROJECT_ID so URLs and the storage API
// don't churn.
const DEFAULT_BUCKET_COLOR = '#6B7280'; // neutral gray — distinguishes from user-picked palette
const DEFAULT_BUCKET_LABEL = 'General';

export const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  flowCount,
  coverThumbnail,
  onClick,
  onRename,
  onDelete,
  onArchive,
  animationIndex = 0,
}) => {
  const isDefaultBucket = project === null;
  const name = isDefaultBucket ? DEFAULT_BUCKET_LABEL : project.name;
  const color = isDefaultBucket ? DEFAULT_BUCKET_COLOR : project.color || '#42C3D7';
  const emoji = isDefaultBucket ? null : project.emoji;

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleAction = useCallback((action?: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    action?.();
  }, []);

  return (
    <div
      className="project-card"
      onClick={onClick}
      style={{
        animationDelay: `${animationIndex * 30}ms`,
        borderTopColor: color,
      }}
    >
      <div className="project-card-cover">
        {coverThumbnail ? (
          <img
            src={coverThumbnail}
            alt={name}
            className="project-card-cover-img"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="project-card-cover-placeholder" style={{ background: color }}>
            {isDefaultBucket ? (
              <PaletteIcon style={{ fontSize: 56, color: '#fff', opacity: 0.85 }} />
            ) : emoji ? (
              <span className="project-card-emoji">{emoji}</span>
            ) : (
              <span className="project-card-initial">{name[0]?.toUpperCase() ?? '·'}</span>
            )}
          </div>
        )}
      </div>

      <div className="project-card-body">
        <div className="project-card-title-row">
          <h3 className="project-card-title" title={name}>
            {emoji && <span className="project-card-title-emoji">{emoji}</span>}
            {name}
          </h3>
          {!isDefaultBucket && (
            <div className="project-card-menu-wrapper" ref={menuRef}>
              <button
                type="button"
                className="project-card-menu-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen((v) => !v);
                }}
                aria-label="Project actions"
              >
                <MoreVertIcon style={{ fontSize: 18 }} />
              </button>
              {menuOpen && (
                <div className="project-card-menu">
                  {onRename && (
                    <button className="project-card-menu-item" onClick={handleAction(onRename)}>
                      Rename
                    </button>
                  )}
                  {onArchive && (
                    <button className="project-card-menu-item" onClick={handleAction(onArchive)}>
                      {project?.archived ? 'Unarchive' : 'Archive'}
                    </button>
                  )}
                  {onDelete && (
                    <button
                      className="project-card-menu-item project-card-menu-item-danger"
                      onClick={handleAction(onDelete)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <p className="project-card-meta">
          {flowCount} {flowCount === 1 ? 'Knod' : 'Knods'}
        </p>
      </div>
    </div>
  );
};

export default ProjectCard;
